import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button, Input, Card, CardContent } from '@nzassa/ui';
import { COLORS } from '@nzassa/shared';
import { useAuthStore } from '@/stores/authStore';
import { useSchoolStore } from '@/stores/schoolStore';
import { api } from '@/lib/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getDefaultRoute } from '@/lib/roleRedirect';

export function AdminLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t('auth.admin.connexion_admin'));
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const schoolCode = searchParams.get('ecole');
  const { school, fetchSchool } = useSchoolStore();

  useEffect(() => {
    if (schoolCode) fetchSchool(schoolCode);
  }, [schoolCode]);

  const [matricule, setMatricule] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // First login state
  const [firstLogin, setFirstLogin] = useState(false);
  const [firstLoginMatricule, setFirstLoginMatricule] = useState('');
  const [firstLoginName, setFirstLoginName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const canSubmit = matricule.trim().length >= 3 && password.length >= 1;

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.login(matricule.trim(), password);
      if (res.requiresFirstLogin) {
        setFirstLogin(true);
        setFirstLoginMatricule(res.matricule ?? matricule.trim());
        setFirstLoginName(res.userName ?? '');
        return;
      }
      if (res.token && res.user) {
        login(res.token, res.user);
        navigate(getDefaultRoute(res.user.role));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.erreur_generique'));
    } finally {
      setLoading(false);
    }
  };

  const handleFirstLogin = async () => {
    if (newPassword.length < 6) {
      setError(t('auth.first_login.mdp_faible'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.first_login.mdp_different'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.firstLogin(firstLoginMatricule, newPassword, confirmPassword);
      if (res.token && res.user) {
        login(res.token, res.user);
        navigate(getDefaultRoute(res.user.role));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.erreur_generique'));
    } finally {
      setLoading(false);
    }
  };

  // ── First Login Screen ──
  if (firstLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-100">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="shadow-xl border-slate-200">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center bg-green-50">
                  <ShieldCheck className="h-7 w-7 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{t('auth.first_login.title')}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {t('auth.first_login.bienvenue')} <span className="font-semibold">{firstLoginName}</span>
                </p>
                <p className="text-xs text-slate-400 mt-1 font-mono">{firstLoginMatricule}</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleFirstLogin(); }} className="space-y-4">
                <div>
                  <label htmlFor="admin-new-password" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.first_login.nouveau_mdp')}</label>
                  <div className="relative">
                    <Input id="admin-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type={showPassword ? 'text' : 'password'} placeholder={t('auth.first_login.placeholder_mdp')} autoComplete="new-password" autoFocus />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? t('auth.masquer_mdp') : t('auth.afficher_mdp')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="admin-confirm-password" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.first_login.confirmer')}</label>
                  <Input id="admin-confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} type="password" autoComplete="new-password" placeholder={t('auth.first_login.retaper')} />
                </div>
                {error && <div role="alert" className="rounded-lg bg-red-50 border border-red-100 p-3 text-xs text-red-600">{error}</div>}
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="ghost" onClick={() => { setFirstLogin(false); setError(''); }} className="flex-1">{t('common.retour')}</Button>
                  <Button type="submit" disabled={!newPassword || !confirmPassword || loading} variant="secondary" className="flex-[2]">
                    {loading ? t('auth.first_login.creation_en_cours') : t('auth.first_login.creer_mdp')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // ── Admin Login Screen (Sober) ──
  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: COLORS.blue }}>
                <img src="/logos/nzassalearn-compact.svg" alt="NzassaLearn" className="h-8 w-8" />
              </div>
              <span className="text-xl font-bold" style={{ color: COLORS.blue }}>NzassaLearn</span>
            </div>
            <p className="text-sm text-slate-500">Administration</p>
            <p className="text-xs font-semibold text-slate-400 mt-1">{school?.name ?? 'ANSUT'}</p>
          </div>

          {/* Login Card */}
          <Card className="shadow-xl border-slate-200">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100">
                  <img src="/logos/nzassalearn-compact.svg" alt="NzassaLearn" className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{t('common.connexion')}</h2>
                  <p className="text-xs text-slate-500">{t('auth.admin.acces_reserve')}</p>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) handleLogin(); }} className="space-y-4">
                <div>
                  <label htmlFor="admin-matricule" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.matricule')}</label>
                  <Input
                    id="admin-matricule"
                    value={matricule}
                    onChange={(e) => setMatricule(e.target.value.toUpperCase())}
                    placeholder="Ex: ADMIN-001"
                    autoComplete="username"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">{t('auth.mot_de_passe')}</label>
                  <div className="relative">
                    <Input
                      id="admin-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.mot_de_passe')}
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? t('auth.masquer_mdp') : t('auth.afficher_mdp')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && <div role="alert" className="rounded-lg bg-red-50 border border-red-100 p-3 text-xs text-red-600">{error}</div>}

                <Button
                  type="submit"
                  disabled={!canSubmit || loading}
                  variant="secondary"
                  className="w-full h-11"
                >
                  {loading ? t('auth.connexion_en_cours') : t('auth.admin.se_connecter')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Student link */}
          <div className="mt-6 text-center">
            <button onClick={() => navigate('/connexion')} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              {t('auth.admin.retour_apprenante')}
            </button>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-slate-400">
            NzassaLearn — Programme ANSUT
          </div>
        </motion.div>
      </div>
    </div>
  );
}
